import React from 'react';
import styled from 'styled-components';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { IconButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { Body, SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import DownloadIcon from '@grnhse/seedling/lib/birch/icons/download';
import PreviewIcon from '@grnhse/seedling/lib/birch/icons/preview';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import DocumentIcon from 'shared/components/documents/document_icon';
import MyGreenhouseProfileNote from 'shared/components/documents/my_greenhouse_profile_note';
import {
  isVirusDetected,
  virusWarningKey,
} from 'shared/components/documents/virus_status';
import { withLocalePrefix } from 'shared/utils/translation';
import type { Document } from './types';

const t = withLocalePrefix('shared.application_history');
const tVirus = withLocalePrefix('shared.document_preview.virus_warning');

const CardContainer = styled.div<{ $clickable: boolean; $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px;
  border: 1px solid ${FullPalette.EVERGREEN_E100};
  border-radius: 4px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  background-color: ${({ $selected }) =>
    $selected ? Tokens.colors.selected : 'transparent'};

  &:hover {
    ${({ $clickable }) =>
      $clickable && `background-color: ${Tokens.colors.hover};`}
  }
`;

const VirusNote = styled(SecondaryBody)<{ $blocked: boolean }>`
  color: ${({ $blocked }) =>
    $blocked ? Tokens.colors.error : FullPalette.GREY_700};
`;

const TruncatedBody = styled(Body)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
`;

type DocumentCardProps = {
  document: Document;
  onClick?: () => void;
  onPreview?: () => void;
  selected?: boolean;
  canDownloadCandidateData?: boolean;
  isRediscovery?: boolean;
};

const DocumentCard: React.FC<DocumentCardProps> = ({
  document,
  onClick,
  onPreview,
  selected = false,
  canDownloadCandidateData,
  isRediscovery,
}) => {
  const fileName = document.display_name || document.file_name;
  // The only in-app guard: the preview never checks virus status, and an in-app download is served.
  const blocked = isVirusDetected(document.virus_status);
  const warningKey = virusWarningKey(document.virus_status);
  const isClickable = Boolean(onClick) && !blocked;
  const canPreview =
    Boolean(onPreview) &&
    !blocked &&
    document.previewable !== false &&
    Boolean(document.previewUrl || document.preview_path);

  const handlePreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    onPreview?.();
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (document.downloadUrl && !blocked) {
      window.open(document.downloadUrl, '_blank');
    }
  };

  return (
    <CardContainer
      $clickable={isClickable}
      $selected={selected}
      onClick={isClickable ? onClick : undefined}
    >
      <Stack spacing={0.5} sx={{ minWidth: 0, flex: '1 1 auto' }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ minWidth: 0 }}
        >
          <DocumentIcon size="md" extension={document.extension} />
          <TruncatedBody title={fileName}>{fileName}</TruncatedBody>
        </Stack>
        {warningKey && (
          <VirusNote
            $blocked={blocked}
            data-provides="document-card-virus-warning"
          >
            {tVirus(warningKey)}
          </VirusNote>
        )}
        {document.fromMyGreenhouseProfile && <MyGreenhouseProfileNote />}
      </Stack>
      {canPreview && (
        <IconButton
          icon={PreviewIcon}
          label={t('preview')}
          onClick={handlePreview}
          size="sm"
        />
      )}
      {(canDownloadCandidateData ?? isRediscovery) && document.downloadUrl && (
        <IconButton
          icon={DownloadIcon}
          label={blocked ? t('download_blocked') : t('download')}
          onClick={handleDownload}
          disabled={blocked}
          size="sm"
        />
      )}
    </CardContainer>
  );
};

export default DocumentCard;
